import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { createDecision, reopenThread, setThreadMuted, snoozeThread } from '../../lib/api.js';
import { SnoozePopover } from './SnoozePopover.js';

/**
 * R15 — triage actions (✓ Done · ⏰ Snooze · 🔇 Mute) shared by the inbox row
 * (hover) and the conversation canvas header, with an undo toast and
 * auto-advance. The provider owns the mutations + the toast so both surfaces
 * behave identically; the action clusters just read the context.
 */

type TriageKind = 'defer' | 'mark_no_response_needed' | 'sent';

interface TriageApi {
  /** Mark no-reply-needed (auto-advances; undo = reopen). */
  done: (threadId: string) => void;
  /** Snooze until ISO (auto-advances; undo = un-snooze). `null` un-snoozes now. */
  snooze: (threadId: string, until: string | null) => void;
  /** Mute the conversation (auto-advances; undo = un-mute). */
  muteThread: (threadId: string) => void;
}

const TriageContext = createContext<TriageApi | null>(null);
export function useTriage(): TriageApi | null {
  return useContext(TriageContext);
}

const TOAST_MS = 8000;

export function TriageProvider({
  onTriaged,
  onReselect,
  children,
}: {
  onTriaged: (currentThreadId: string, kind: TriageKind) => void;
  /** Re-select a thread (used to RESTORE selection when an action fails). */
  onReselect?: (threadId: string) => void;
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const [toast, setToast] = useState<{ id: number; message: string; undo?: () => void } | null>(
    null,
  );
  const timerRef = useRef<number | undefined>(undefined);
  const toastSeqRef = useRef(0);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const dismiss = useCallback(() => {
    window.clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['threads'] });
    void qc.invalidateQueries({ queryKey: ['followups'] });
  }, [qc]);

  // R15 review — key the toast on an incrementing id so a repeated triage REMOUNTS
  // the role=status node (re-announces); 8s gives the keyboard path time to reach Undo.
  const showToast = useCallback((message: string, undo?: () => void) => {
    window.clearTimeout(timerRef.current);
    toastSeqRef.current += 1;
    setToast({ id: toastSeqRef.current, message, undo });
    timerRef.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  // Fire the action; on success invalidate + raise an undo toast. R15 review —
  // on FAILURE, invalidate too (the still-actionable thread reappears) AND offer to
  // RESTORE the selection (the optimistic advance moved past a thread that wasn't
  // actually triaged), rather than a silent dead-end.
  const run = useCallback(
    (action: Promise<unknown>, okMessage: string, undo: () => void, threadId: string) => {
      action.then(
        () => {
          invalidate();
          showToast(okMessage, () => {
            undo();
            dismiss();
          });
        },
        () => {
          invalidate();
          showToast(
            'Action failed — restored',
            onReselect
              ? () => {
                  onReselect(threadId);
                  dismiss();
                }
              : undefined,
          );
        },
      );
    },
    [invalidate, showToast, dismiss, onReselect],
  );

  const api = useMemo<TriageApi>(
    () => ({
      done: (threadId) => {
        onTriaged(threadId, 'mark_no_response_needed'); // advance off the current cache first
        run(
          createDecision(threadId, { kind: 'mark_no_response_needed' }),
          'Marked done',
          () => void reopenThread(threadId).then(invalidate),
          threadId,
        );
      },
      snooze: (threadId, until) => {
        if (until === null) {
          // un-snooze: route failures through a toast like every other action.
          snoozeThread(threadId, null).then(invalidate, () => showToast('Action failed'));
          return;
        }
        onTriaged(threadId, 'defer');
        run(
          snoozeThread(threadId, until),
          'Snoozed',
          () => void snoozeThread(threadId, null).then(invalidate),
          threadId,
        );
      },
      muteThread: (threadId) => {
        onTriaged(threadId, 'defer');
        run(
          setThreadMuted(threadId, true),
          'Muted',
          () => void setThreadMuted(threadId, false).then(invalidate),
          threadId,
        );
      },
    }),
    [onTriaged, run, invalidate, showToast],
  );

  return (
    <TriageContext.Provider value={api}>
      {children}
      {toast ? (
        <UndoToast key={toast.id} message={toast.message} onUndo={toast.undo} onDismiss={dismiss} />
      ) : null}
    </TriageContext.Provider>
  );
}

export function UndoToast({
  message,
  onUndo,
  onDismiss,
}: {
  message: string;
  onUndo?: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="undo-toast" role="status" data-testid="undo-toast">
      <span className="undo-toast-msg">{message}</span>
      {onUndo ? (
        <button
          type="button"
          className="undo-toast-undo"
          data-testid="undo-toast-undo"
          onClick={onUndo}
        >
          Undo
        </button>
      ) : null}
      <button
        type="button"
        className="undo-toast-x"
        aria-label="Dismiss"
        data-testid="undo-toast-dismiss"
        onClick={onDismiss}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * A small popover anchored to `anchorRef`, portalled to <body> so it is never
 * clipped by the virtualized list's `overflow:auto`. Closes on Escape / outside
 * pointer / scroll / resize; moves focus into the popover on open and restores it
 * to the anchor on close. (Reused by R16's bulk-overflow menu.)
 */
export function AnchoredPopover({
  anchorRef,
  open,
  onClose,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const a = anchorRef.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 184) });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchorRef.current?.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // R15 review — the anchor lives in a scrollable virtualized list; close on
    // scroll/resize so the fixed-position popover never drifts off its button
    // (and so a row unmount mid-open can't strand it). capture=true catches the
    // inner list scroll.
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [open, onClose, anchorRef]);

  // R15 review — move focus into the popover on open; restore to the anchor on close.
  useEffect(() => {
    if (!open || !pos) return;
    ref.current?.querySelector<HTMLElement>('button, [tabindex]')?.focus();
    return () => anchorRef.current?.focus?.();
  }, [open, pos, anchorRef]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Snooze"
      className="anchored-popover"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 60 }}
    >
      {children}
    </div>,
    document.body,
  );
}

export function TriageActions({
  threadId,
  variant,
  tabbable = true,
}: {
  threadId: string;
  variant: 'row' | 'canvas';
  /** Row clusters pass `selected` so only the active row's actions are tabbable
   * (else every hidden row injects 3 phantom tab stops). R15 review. */
  tabbable?: boolean;
}) {
  const triage = useTriage();
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const snoozeBtnRef = useRef<HTMLButtonElement>(null);
  if (!triage) return null;
  const ti = tabbable ? 0 : -1;
  // No stopPropagation needed: these buttons are SIBLINGS of the row's select
  // button (not nested), so their clicks don't select the row.
  return (
    <div className={`triage-actions triage-${variant}`} data-testid="triage-actions">
      <button
        type="button"
        className="triage-btn triage-done"
        data-testid="triage-done"
        tabIndex={ti}
        title="Done — no reply needed"
        aria-label="Mark done"
        onClick={() => triage.done(threadId)}
      >
        ✓
      </button>
      <button
        ref={snoozeBtnRef}
        type="button"
        className="triage-btn triage-snooze"
        data-testid="triage-snooze"
        tabIndex={ti}
        title="Snooze"
        aria-label="Snooze"
        aria-expanded={snoozeOpen}
        onClick={() => setSnoozeOpen((o) => !o)}
      >
        ⏰
      </button>
      <button
        type="button"
        className="triage-btn triage-mute"
        data-testid="triage-mute"
        tabIndex={ti}
        title="Mute"
        aria-label="Mute conversation"
        onClick={() => triage.muteThread(threadId)}
      >
        🔇
      </button>
      <AnchoredPopover
        anchorRef={snoozeBtnRef}
        open={snoozeOpen}
        onClose={() => setSnoozeOpen(false)}
      >
        <SnoozePopover
          replyAware
          onPick={(until) => {
            triage.snooze(threadId, until);
            setSnoozeOpen(false);
          }}
        />
      </AnchoredPopover>
    </div>
  );
}
